import {Grid} from '@logistics-frontend/blocks'
import {Divider} from '@logistics-frontend/blocks/lpc/divider'
import {GridHeader} from '@logistics-frontend/blocks/lpc/grid-header'
import {useHelperClasses} from '@logistics-frontend/blocks/lpc/input/classes'
import {ToggleButtonGroupProps, TogglePlateGroup} from '@logistics-frontend/blocks/lpc/toggle-button'
import {useI18n} from '@logistics-frontend/core/i18n'
import {get} from '@logistics-frontend/utils'
import {FormHelperText} from '@material-ui/core'
import {MaxTariff} from '@yandex-taxi/js-integration-api'
import {useTariff} from '@yandex-taxi/react-integration-api'
import React, {useCallback, useEffect} from 'react'

import {useTariffGoal} from '../../../shared/metrika'
import {Requirements} from '../requirements'
import {useClasses, useTogglePlateGroupClasses} from './classes'
import TariffPlate from './TariffPlate'
import {useUpdateURLPath} from './Tariffs.hooks'

type Props = {
  tariffs: MaxTariff[]
}

const Tariffs: React.FC<Props> = ({tariffs}) => {
  const t = useI18n()
  const classes = useClasses()
  const togglePlateGroupClasses = useTogglePlateGroupClasses()
  const [{value, error, touched}, {setValue}] = useTariff()
  const changeHandler = useCallback<ToggleButtonGroupProps['onChange']>(
    (_, tariff: string | null) => tariff && setValue(tariff),
    [setValue],
  )
  const helperClasses = useHelperClasses()
  const helperText: string | undefined = touched && error ? error : undefined
  const supportedRequirements = tariffs.find(t => t.class === value)?.supported_requirements
  const cargoTypeRequirementName = supportedRequirements?.find(
    ({name}) => name === 'cargo_type_int' || name === 'cargo_type',
  )?.name
  const isTariffSelected = Boolean(value)
  // на сервере запрос /zoneInfo по дефолту уходит с мск, и потом может дозапросить на фронте.
  // Проверка isSelectedTariffUnavailableInCurrentZone нужна, на случай, если в актульаной зоне есть не все тарифы
  const isSelectedTariffUnavailableInCurrentZone = !tariffs.some(tariff => tariff.class === value)
  const maybeDefaultTariff =
    !isTariffSelected || isSelectedTariffUnavailableInCurrentZone ? get('[0].class', tariffs) : undefined

  useTariffGoal(value)
  useUpdateURLPath(value, cargoTypeRequirementName)

  useEffect(() => {
    if (maybeDefaultTariff) {
      setValue(maybeDefaultTariff)
    }
  }, [maybeDefaultTariff, setValue])

  return (
    <>
      <Grid spacing={1} container>
        <GridHeader className={classes.boldTitle}>{t('order.tariffs.title', 'Тарифы')}</GridHeader>
        <Grid xs={12} item>
          <TogglePlateGroup classes={togglePlateGroupClasses} onChange={changeHandler} value={value} exclusive>
            {tariffs.map(tariff => (
              <TariffPlate key={tariff.class} currentValue={value} tariff={tariff} value={tariff.class} />
            ))}
          </TogglePlateGroup>
          {helperText && (
            <FormHelperText classes={helperClasses} error>
              {helperText}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      {supportedRequirements && <Requirements requirements={supportedRequirements} tariff={value} />}
    </>
  )
}

export default Tariffs
